import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { appsLanguagePositionSelected } from '../../helpers/app'
import { images } from '../../images'
import { StorageService } from '../../services'
import literalsService, { Language } from '../../services/literals-service'
import { setChangeLanguageAction } from '../../store/actions'
import { IStoreState } from '../../store/states'
import StyledDiv from './styles'

interface IProps extends DispatchProp<AnyAction> {
  language: string
}

interface IState {
  isOpen: boolean
}

class LanguageSelector extends PureComponent<IProps, IState> {

  private container = React.createRef<HTMLDivElement>()

  constructor(props: IProps) {
    super(props)

    this.state = { isOpen: false }
  }

  private get languages(): string[] {
    return appsLanguagePositionSelected?.languages ?? []
  }

  // Inline so both icons take the pill's colour through currentColor.
  private get globeIcon(): ReactNode {
    return (
      <svg className="globe" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="currentColor" d="M24 44q-4.2 0-7.85-1.575Q12.5 40.85 9.8 38.15q-2.7-2.7-4.25-6.375Q4 28.1 4 23.9t1.55-7.825Q7.1 12.45 9.8 9.75t6.35-4.225Q19.8 4 24 4q4.2 0 7.85 1.525Q35.5 7.05 38.2 9.75q2.7 2.7 4.25 6.325Q44 19.7 44 23.9t-1.55 7.875Q40.9 35.45 38.2 38.15t-6.35 4.275Q28.2 44 24 44Zm7.9-27.5h7.5q-1.65-3.45-4.525-5.75Q32 8.45 28.25 7.5q1.25 1.85 2.125 4t1.525 5Zm-12.7 0h9.7q-.55-2.65-1.85-5.125T24 7q-1.6 1.35-2.7 3.55-1.1 2.2-2.1 5.95ZM7.6 28.45h7.95q-.15-1.35-.175-2.425-.025-1.075-.025-2.125 0-1.25.05-2.225.05-.975.2-2.175h-8q-.35 1.2-.475 2.15T7 23.9q0 1.3.125 2.325.125 1.025.475 2.225ZM19.75 40.5q-1.25-1.9-2.15-4.1-.9-2.2-1.5-4.95H8.6Q10.5 35 13 37.025q2.5 2.025 6.75 3.475ZM8.6 16.5h7.55q.55-2.7 1.4-4.825.85-2.125 2.15-4.125-3.75.95-6.55 3.2T8.6 16.5ZM24 41.1q1.75-1.8 2.925-4.125Q28.1 34.65 28.85 31.45H19.2q.7 3 1.875 5.4Q22.25 39.25 24 41.1Zm-5.35-12.65H29.4q.2-1.55.25-2.525.05-.975.05-2.025 0-1-.05-1.925T29.4 19.5H18.65q-.2 1.55-.25 2.475-.05.925-.05 1.925 0 1.05.05 2.025.05.975.25 2.525Zm9.65 12q3.6-1.15 6.475-3.45 2.875-2.3 4.625-5.55h-7.45q-.65 2.7-1.525 4.9-.875 2.2-2.125 4.1Zm4.1-12h8q.35-1.2.475-2.225Q41 25.2 41 23.9q0-1.3-.125-2.25T40.4 19.5h-7.95q.15 1.75.2 2.675.05.925.05 1.725 0 1.1-.075 2.075-.075.975-.225 2.475Z" />
      </svg>
    )
  }

  private get chevronIcon(): ReactNode {
    return (
      <svg className={ `chevron ${this.state.isOpen ? 'open' : ''}` } viewBox="0 0 24 25" aria-hidden="true">
        <path fill="currentColor" d="M20.2083 6.46413L22 8.2558L12 18.2558L2 8.29746L3.79167 6.5058L12 14.7141L20.2083 6.46413Z" />
      </svg>
    )
  }

  private get options(): ReactNode {
    const { language } = this.props

    return this.languages.map((option: string) => (
      <button
        key={ `language-option-${option}` }
        type="button"
        className={ `option ${option === language ? 'selected' : ''}` }
        onClick={ () => this.handleLanguagePress(option) }
      >
        <img className="flag" src={ images[option as keyof typeof images] } alt={ option } />
        <span> { literalsService.getInLanguage(option, option, true) } </span>
        { option === language && <img className="check" src={ images['checkLanguage'] } alt="selected" /> }
      </button>
    ))
  }

  public componentDidMount(): void {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  public componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  public render(): ReactNode {
    const { language } = this.props
    const { isOpen } = this.state

    if (!appsLanguagePositionSelected?.showSelector || this.languages.length < 2) return null

    return (
      <StyledDiv ref={ this.container }>
        <button type="button" className="pill" onClick={ this.toggle } aria-expanded={ isOpen }>
          { this.globeIcon }
          <span> { literalsService.getInLanguage(language, language, true) } </span>
          { this.chevronIcon }
        </button>
        { isOpen && <div className="options"> { this.options } </div> }
      </StyledDiv>
    )
  }

  private toggle = (): void => {
    this.setState({ isOpen: !this.state.isOpen })
  }

  private handleClickOutside = (event: MouseEvent): void => {
    if (!this.state.isOpen) return
    if (this.container.current?.contains(event.target as Node)) return

    this.setState({ isOpen: false })
  }

  // No session to save it against: the choice lives in the store and the browser.
  private handleLanguagePress = (language: string): void => {
    literalsService.setLanguage(language)
    StorageService.setLanguage(language as Language)
    this.props.dispatch(setChangeLanguageAction(language))
    this.setState({ isOpen: false })
  }
}

const mapStateToProps = ({ configuration }: IStoreState) => ({
  language: configuration.language
})

export default connectRedux(mapStateToProps, LanguageSelector)
